import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Tickets from "./pages/Tickets";
import TicketDetail from "./pages/TicketDetail";
import Devices from "./pages/Devices";
import DeviceInventory from "./pages/EdrInventory";
import EdrConsole from "./pages/EdrConsole";
import EdrAgentDetail from "./pages/EdrAgentDetail";
import Device360 from "./pages/Device360";
import Servers from "./pages/Servers";
import Server360 from "./pages/Server360";
import Analytics from "./pages/Analytics";
import Clients from "./pages/Clients";
import Agents from "./pages/Agents";
import Client360 from "./pages/Client360";
import Contact360 from "./pages/Contact360";
import ProjectDetails from "./pages/ProjectDetails";
import AdminSettings from "./pages/AdminSettings";
import PortalApp from "./portal/PortalApp";
import ResetPassword from "./pages/ResetPassword"; // ← NEW
import StatusPage from "./pages/StatusPage"; // public system status
import DevTickets     from "./pages/DevTickets";
import Infrastructure from "./pages/Infrastructure";
import KnowledgeBase from "./pages/KnowledgeBase";
import AdminReview from "./pages/AdminReview";
import ClientDatabase from "./pages/ClientDatabase";import { ROLE_ACCESS } from "./config/roles";
import { ThemeStyles } from "./theme";
import { ThemeProvider } from "./context/ThemeContext";
import MobileAppPrompt from "./components/MobileAppPrompt";
export { ROLE_ACCESS };

// Error boundary — shows the actual error instead of a blank screen
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null, info: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { this.setState({ info }); console.error("React error:", error, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, color: "#f87171", background: "var(--t-page, #111b2f)", minHeight: "100vh", fontFamily: "monospace", fontSize: 13 }}>
          <h2 style={{ color: "#fca5a5", marginBottom: 12 }}>Page crashed</h2>
          <pre style={{ background: "#1f2937", padding: 12, borderRadius: 6, whiteSpace: "pre-wrap", color: "#fbbf24" }}>{String(this.state.error?.stack || this.state.error)}</pre>
          <pre style={{ background: "#1f2937", padding: 12, borderRadius: 6, whiteSpace: "pre-wrap", color: "#94a3b8", marginTop: 8 }}>{this.state.info?.componentStack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// =============================================================
//  ROUTE GUARD — Internal agents only
// =============================================================
const PrivateRoute = ({ children, page, requireRole }) => {
  const { agent, loading } = useAuth();

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"var(--t-page, #111b2f)" }}>
      <div style={{ width:32, height:32, border:"3px solid #4f6fff", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
    </div>
  );

  if (!agent) return <Navigate to="/login" replace />;
  if (agent.role === "contact") return <Navigate to="/login" replace />;

  // Strict role gate (e.g. admin-only pages)
  if (requireRole) {
    const roles = agent.roles?.length ? agent.roles : [agent.role];
    const need  = Array.isArray(requireRole) ? requireRole : [requireRole];
    if (!need.some(r => roles.includes(r))) return <Navigate to="/dashboard" replace />;
  }

  // If a specific page is required, check role access
  if (page) {
    const allowed = ROLE_ACCESS[agent.role] || [];
    if (!allowed.includes(page)) return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// =============================================================
//  APP
// =============================================================
function App() {
  return (
    <BrowserRouter>
      <ThemeStyles />
      <Routes>

        {/* ── Public ─────────────────────────────────────────── */}
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} /> {/* ← NEW */}
        <Route path="/status" element={<StatusPage />} /> {/* public system status */}

        {/* ── White-labeled Customer Portal ──────────────────── */}
        {/* URL: /clientportal/acme-corp  */}
        <Route path="/clientportal/:identifier/*" element={<PortalApp />} />

        {/* ── Internal Agent App ──────────────────────────────── */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />

          {/* All roles */}
          <Route path="dashboard" element={
            <PrivateRoute page="dashboard"><Dashboard /></PrivateRoute>
          } />

          {/* All roles */}
          <Route path="tickets" element={
            <PrivateRoute page="tickets"><Tickets /></PrivateRoute>
          } />
          <Route path="tickets/:id" element={
            <PrivateRoute page="tickets"><TicketDetail /></PrivateRoute>
          } />

          {/* All roles */}
          <Route path="clients" element={
            <PrivateRoute page="clients"><Clients /></PrivateRoute>
          } />
          <Route path="clients/:id" element={
            <PrivateRoute page="clients"><Client360 /></PrivateRoute>
          } />
          <Route path="contacts/:id" element={
            <PrivateRoute page="clients"><Contact360 /></PrivateRoute>
          } />
          <Route path="dev-tickets" element={
            <PrivateRoute page="dev-tickets"><DevTickets /></PrivateRoute>
          } />
          <Route path="infrastructure" element={
            <PrivateRoute page="infrastructure"><ErrorBoundary><Infrastructure /></ErrorBoundary></PrivateRoute>
          } />
          <Route path="projects/:id" element={
            <PrivateRoute requireRole="admin"><ErrorBoundary><ProjectDetails /></ErrorBoundary></PrivateRoute>
          } />

          {/* All roles */}
          <Route path="devices" element={
            <PrivateRoute page="devices"><Devices /></PrivateRoute>
          } />

          <Route path="inventory" element={
            <PrivateRoute page="devices"><DeviceInventory /></PrivateRoute>
          } />
          <Route path="edr-inventory" element={
            <PrivateRoute page="devices"><DeviceInventory /></PrivateRoute>
          } />
          <Route path="edr-console" element={
            <PrivateRoute page="devices"><EdrConsole /></PrivateRoute>
          } />
          <Route path="edr-agent/:agentId" element={
            <PrivateRoute page="devices"><EdrAgentDetail /></PrivateRoute>
          } />
          <Route path="devices/:deviceId" element={
            <PrivateRoute page="devices"><Device360 /></PrivateRoute>
          } />

          {/* Server RMM — staff-only (admin/manager/technician/dev) */}
          <Route path="server-rmm" element={
            <PrivateRoute page="server-rmm"><ErrorBoundary><Servers /></ErrorBoundary></PrivateRoute>
          } />
          <Route path="server-rmm/:serverId" element={
            <PrivateRoute page="server-rmm"><ErrorBoundary><Server360 /></ErrorBoundary></PrivateRoute>
          } />

          {/* All staff — internal Client & Website Registry */}
          <Route path="client-database" element={
            <PrivateRoute page="client-database"><ErrorBoundary><ClientDatabase /></ErrorBoundary></PrivateRoute>
          } />
          <Route path="client-database/clients/:id" element={
            <PrivateRoute page="client-database"><ErrorBoundary><ClientDatabase /></ErrorBoundary></PrivateRoute>
          } />
          {/* Admin / Manager only */}
          <Route path="analytics" element={
            <PrivateRoute page="analytics"><Analytics /></PrivateRoute>
          } />

          {/* All staff roles */}
          <Route path="knowledge-base" element={
            <PrivateRoute page="knowledge-base"><KnowledgeBase /></PrivateRoute>
          } />
          <Route path="knowledge-base/:id" element={
            <PrivateRoute page="knowledge-base"><KnowledgeBase /></PrivateRoute>
          } />

          {/* Admin / Manager only */}
          <Route path="agents" element={
            <PrivateRoute page="agents"><Agents /></PrivateRoute>
          } />

          {/* Admin only */}
          <Route path="admin-review" element={
            <PrivateRoute page="admin-review"><AdminReview /></PrivateRoute>
          } />

          {/* Admin / Manager only */}
          <Route path="settings" element={
            <PrivateRoute page="settings"><AdminSettings /></PrivateRoute>
          } />
        </Route>

        {/* ── Embed routes — no Layout/sidebar, used by desktop app iframe ── */}
        <Route path="/embed" element={<div style={{ height:"100vh", overflow:"hidden", background:"var(--t-page, #111b2f)", display:"flex", flexDirection:"column" }}><Outlet /></div>}>
          <Route path="infrastructure" element={
            <PrivateRoute page="infrastructure"><Infrastructure /></PrivateRoute>
          } />
          <Route path="dev-tickets" element={
            <PrivateRoute page="dev-tickets"><DevTickets /></PrivateRoute>
          } />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />

      </Routes>
    </BrowserRouter>
  );
}

export default function AppWithProviders() {
  return (
    <ThemeProvider>
      <App />
      <MobileAppPrompt />
    </ThemeProvider>
  );
}
